import { useEffect, useReducer, type ReactNode } from 'react';
import type { ColorScheme } from '../../visualization/types';
import {
  createUiState,
  hashOfView,
  uiReducer,
  UiDispatchContext,
  UiStateContext,
  viewOfHash,
  type AppView,
} from './ui-state';

const preferredScheme = (): ColorScheme =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const currentView = (): AppView =>
  typeof window === 'undefined' ? 'analysis' : viewOfHash(window.location.hash);

export interface UiStateProviderProps {
  readonly children: ReactNode;
  readonly initialColorScheme?: ColorScheme;
}

/**
 * Estado de UI separado del documento; aplica el tema al elemento raiz (`.dark`) y
 * sincroniza la vista activa con `location.hash` (atras/adelante del navegador).
 */
export function UiStateProvider({ children, initialColorScheme }: UiStateProviderProps) {
  const [state, dispatch] = useReducer(
    uiReducer,
    initialColorScheme ?? null,
    (scheme: ColorScheme | null) => createUiState(scheme ?? preferredScheme(), currentView()),
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', state.colorScheme === 'dark');
  }, [state.colorScheme]);

  useEffect(() => {
    const onHashChange = () => {
      dispatch({ type: 'set-active-view', view: currentView() });
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  useEffect(() => {
    if (currentView() === state.activeView) return;
    window.location.hash = hashOfView(state.activeView);
  }, [state.activeView]);

  return (
    <UiDispatchContext value={dispatch}>
      <UiStateContext value={state}>{children}</UiStateContext>
    </UiDispatchContext>
  );
}
